import React from "react";
import { withTranslation } from "react-i18next";
import withRouter from "../../../components/Common/withRouter";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCreateContactUS } from "../../../api/contact-us";
import { useSelector } from "react-redux";
import {
  selectRole,
  selectHasCompletedOnBoarding,
} from "../../../store/auth/selectors";
import {
  JOBARK_PHONE_DISPLAY,
  JOBARK_PHONE_TEL,
  getContactDefaultSubject,
  getContactSubjectOptions,
  getContactSubjectPromptType,
} from "../../../constants/contact";

const ContactUs = (props) => {
  const { t } = props;

  document.title = t("contactUsTitle", "Contact Us | JobArk");
  const role = useSelector(selectRole);
  const hasCompletedOnBoarding = useSelector(selectHasCompletedOnBoarding);
  const isWorker = role === "worker";

  const subjectOptions = getContactSubjectOptions(role);
  const defaultSubject = getContactDefaultSubject(role);

  const { mutate, isPending, isSuccess, isError, error } = useCreateContactUS();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      subject: defaultSubject,
      message: ""
    },
    validationSchema: Yup.object({
      subject: Yup.string().required(t("contactUsFormRequired", "This field is required.")),
      message: Yup.string()
        .trim()
        .min(
          10,
          t("contactUsFormMinChars", {
            defaultValue: "Please type at least {{n}} characters",
            n: 10,
          })
        )
        .required(t("contactUsFormRequired", "This field is required."))
    }),
    onSubmit: (values, { resetForm }) => {
      mutate(
        { subject: values.subject, message: values.message },
        {
          onSuccess: () =>
            resetForm({ values: { subject: defaultSubject, message: "" } }),
        }
      );
    }
  });

  const promptType = getContactSubjectPromptType(role, formik.values.subject);

  const staffingGuidanceItems = [
    t("contactUsGuidanceStaffingRole", "Job role(s) required"),
    t("contactUsGuidanceStaffingWorkers", "Number of workers"),
    t("contactUsGuidanceStaffingDates", "Date(s) and shift times"),
    t("contactUsGuidanceStaffingLocation", "Site location"),
    t("contactUsGuidanceStaffingDuration", "Duration of the booking"),
    t(
      "contactUsGuidanceStaffingQuals",
      "Any qualifications, licences or experience required"
    ),
    t("contactUsGuidanceStaffingAdditional", "Any additional information"),
  ];

  return (
    <div className="page-content contact-us-page">
      <div className="container-fluid">
        <Container className="my-5">
          <Row className="g-4 align-items-start">
            <Col lg={6}>
              <div className="mb-4">
                <h3 className="contact-us-heading">
                  {t("contactUsPageTitle", "Contact Us")}
                </h3>
                <h5 className="mt-3 mb-2">
                  {isWorker
                    ? t("contactUsWorkerIntroTitle", "Need help?")
                    : t(
                        "contactUsClientIntroTitle",
                        "Need help or looking for staff?"
                      )}
                </h5>
                <p className="text-muted contact-us-intro mb-0">
                  {isWorker
                    ? t(
                        "contactUsWorkerIntroSubtitle",
                        "Whether you have a question about an assignment, timesheet, pay, compliance or require general assistance, our team is here to help."
                      )
                    : t(
                        "contactUsClientIntroSubtitle",
                        "Whether you have a question about an existing booking, need to request temporary staff, have an invoice query or require general assistance, our team is here to help."
                      )}
                </p>
              </div>

              <div className="mb-4">
                <h5 className="contact-us-section-title">
                  {t("contactUsContactInformation", "Contact Information")}
                </h5>

                <div className="contact-us-info-block">
                  <h6 className="contact-us-info-label">
                    {t("contactUsLeftRegisteredOffice", "Registered Office")}
                  </h6>
                  <p className="mb-0">
                    71–75 Shelton Street, Covent Garden,
                    <br />
                    London, WC2H 9JQ
                  </p>
                </div>

                <div className="contact-us-info-block">
                  <h6 className="contact-us-info-label">
                    {t("contactUsLeftEmail", "Email")}
                  </h6>
                  <a href="mailto:info@jobark.co.uk">info@jobark.co.uk</a>
                </div>

                <div className="contact-us-info-block">
                  <h6 className="contact-us-info-label">
                    {t("contactUsLeftPhone", "Telephone")}
                  </h6>
                  <a href={`tel:${JOBARK_PHONE_TEL}`}>{JOBARK_PHONE_DISPLAY}</a>
                </div>

                <div className="contact-us-info-block">
                  <h6 className="contact-us-info-label">
                    {t("contactUsBusinessHours", "Business Hours")}
                  </h6>
                  <p className="mb-0">
                    {t(
                      "contactUsBusinessHoursValue",
                      "Monday – Friday | 9:00am – 5:00pm"
                    )}
                  </p>
                </div>
              </div>

              <div className="contact-us-urgent">
                <h5 className="contact-us-section-title">
                  {isWorker
                    ? t(
                        "contactUsWorkerUrgentTitle",
                        "Urgent Assignment Support"
                      )
                    : t(
                        "contactUsClientUrgentTitle",
                        "Urgent Staffing Requirements"
                      )}
                </h5>
                <p className="text-muted mb-0">
                  {isWorker
                    ? t(
                        "contactUsWorkerUrgentBody",
                        "If your enquiry relates to today’s assignment, a shift you’re due to attend, or any urgent issue that may affect your availability, please contact us by telephone as soon as possible."
                      )
                    : t(
                        "contactUsClientUrgentBody",
                        "If you require urgent or same-day staffing support, please contact us by telephone so we can assist you as quickly as possible."
                      )}
                </p>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-sm border-0 contact-us-form-card">
                <CardBody className="p-4">
                  <h5 className="fw-bold mb-1">
                    {t("contactUsFormTitle", "Send A Message")}
                  </h5>
                  <p className="text-muted mb-4">
                    {t(
                      "contactUsFormSubtitle",
                      "Fill in a few quick details and our team will get back to you."
                    )}
                  </p>

                  {isSuccess && (
                    <Alert color="success" className="py-2">
                      {t(
                        "contactUsFormSuccess",
                        "Thanks! Your message has been sent."
                      )}
                    </Alert>
                  )}
                  {isError && (
                    <Alert color="danger" className="py-2">
                      {error?.response?.data?.message?.error?.[0] ||
                        t("contactUsFormError", "Failed to send message.")}
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                      return false;
                    }}
                  >
                    <Row className="g-3">
                      <Col md={12}>
                        <Label className="form-label">
                          {t("contactUsFormSubject", "Subject")}
                        </Label>
                        <Input
                          type="select"
                          name="subject"
                          value={formik.values.subject}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.subject && !!formik.errors.subject
                          }
                        >
                          {subjectOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {t(opt.labelKey, opt.defaultLabel)}
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{formik.errors.subject}</FormFeedback>
                      </Col>

                      <Col md={12}>
                        {promptType === "bookingReference" && (
                          <div className="contact-us-guidance">
                            <p className="mb-0">
                              {t(
                                "contactUsGuidanceBookingReference",
                                "Please include your Booking Reference within the message."
                              )}
                            </p>
                          </div>
                        )}

                        {promptType === "assignmentReference" && (
                          <div className="contact-us-guidance">
                            <p className="mb-0">
                              {t(
                                "contactUsGuidanceAssignmentReference",
                                "Please include your Assignment Reference within the message."
                              )}
                            </p>
                          </div>
                        )}

                        {promptType === "staffingGuidance" && (
                          <div className="contact-us-guidance">
                            <p>
                              {t(
                                "contactUsGuidanceStaffingIntro",
                                "Please include as much of the following information as possible:"
                              )}
                            </p>
                            <ul>
                              {staffingGuidanceItems.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <Label className="form-label">
                          {t("contactUsFormMessage", "Message")}
                        </Label>
                        <Input
                          type="textarea"
                          rows="6"
                          name="message"
                          placeholder={t(
                            "contactUsFormMessagePH",
                            "Write your message…"
                          )}
                          value={formik.values.message}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.message && !!formik.errors.message
                          }
                        />
                        <FormFeedback>{formik.errors.message}</FormFeedback>
                      </Col>

                      <Col md={12} className="d-grid">
                        <Button
                          type="submit"
                          className="contact-us-submit-btn"
                          disabled={
                            isPending ||
                            (!hasCompletedOnBoarding && role === "client")
                          }
                        >
                          {isPending
                            ? t("contactUsFormSending", "Sending…")
                            : t("contactUsFormSend", "Send Message")}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default withRouter(withTranslation()(ContactUs));
